import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loading } from "src/components/utils/Loading";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useProductContext } from "./ProductContext";
import { URLHandler } from "src/services/URLHandler.ts";
import { HintBox } from "src/components/HintBox.tsx";
import { useApplicationParameters } from "src/api/client-api/appParams";
import { ScoreCard } from "@foodpilot/foods";
import { Fragment } from "react/jsx-runtime";
import { ScoreSummary } from "src/components/Score/ScoreCardContent/ScoreSummary";
import {
    ProductReferenceScore,
    ProductReferenceScores,
} from "src/api/client-api/productReferenceScores/useGetProductReferenceScores";

export const ProductScores = () => {
    const { t } = useTranslation();
    const {
        product,
        productReference,
        productReferenceScores = [],
        allScoreWithVariations,
        scoreState,
        ssq,
        post,
    } = useProductContext();
    const theme = useTheme();
    const { isFeatureActivated } = useApplicationParameters();
    const { scoreTypes, scores, getScoresByType, selectedCompany } = useFoodpilotContext();

    const navigate = useNavigate();

    if (!scoreTypes || !scores || !product) {
        return <Loading />;
    }

    const productsScores = getScoresByType("product");

    if (productsScores.length === 0) {
        return (
            <Stack gap="28px" sx={{ padding: "44px 52px" }}>
                <Box sx={{ textAlign: "center", padding: "28px" }}>{t("product.no-score")}</Box>
            </Stack>
        );
    }

    const mappedReferenceScores = productReferenceScores.reduce(
        (acc, score) => {
            if (score.score) {
                acc[score.score.id] = score.value ?? undefined;
            }
            return acc;
        },
        {} as Record<NonNullable<ProductReferenceScore["score"]>["id"], ProductReferenceScore["value"]>,
    );

    const hasSsq = product.ssqId !== undefined;

    const isDistributionActive = isFeatureActivated("FS-score-details-distribution");

    const isDataAvailable = ssq !== undefined && post !== undefined;
    const canDrilldown = isDistributionActive && isDataAvailable;

    return (
        <Stack gap="1.5rem">
            {productReference && (
                <Box
                    sx={{
                        marginTop: "-1rem",
                        display: "flex",
                        bgcolor: theme.palette.background.paper,
                        width: "fit-content",
                        padding: ".5rem",
                        borderRadius: "6px",
                        gap: "8px",
                        border: "1px solid #E7E6E4",
                    }}
                >
                    <Typography variant="h5">{t("Référence")}:</Typography>{" "}
                    <Typography variant="body-medium">{productReference?.name}</Typography>
                </Box>
            )}
            {productReference !== null && !hasSsq && productReferenceScores.length > 0 && (
                <Box>
                    <HintBox title={t("product.noSsq.info.title")} content={t("product.noSsq.info.body")} />
                </Box>
            )}

            <Stack gap="28px">
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gridAutoRows: "1fr",
                        gap: "28px",
                    }}
                >
                    {productsScores.map((score) => {
                        const scoreOpts = allScoreWithVariations.filter(
                            (scoreVariation) => scoreVariation.group === score.title,
                        );
                        const selectedScore = scoreOpts.find((filteredScore) => {
                            const scoreVariation = scoreState.selectedVariations[score.id];
                            const doesScoreMatch = scoreVariation !== undefined;
                            const doesVariationMatch =
                                doesScoreMatch === false ? false : filteredScore.variationId === scoreVariation;

                            return doesScoreMatch && doesVariationMatch;
                        });

                        if (scoreOpts.length === 0) return;
                        const selectedOption = selectedScore ?? scoreOpts[0];

                        const scoreValue = product.scores?.[score.id];
                        const prevScore = product?.prevScores?.[score.id]?.score;
                        const prevCampaign = product.prevCampaignName;
                        return (
                            <Fragment key={`${score.id}-${selectedOption.rowId}`}>
                                <ScoreCard
                                    header={{
                                        selectedItemId: selectedOption.rowId,
                                        options: scoreOpts.map((d) => ({ ...d, id: d.rowId })),
                                    }}
                                    moreDetailAction={
                                        canDrilldown === false ? undefined : (
                                            () => {
                                                if (selectedCompany === null) return;
                                                navigate(
                                                    URLHandler.product.score(selectedCompany.id, product.id, score.id),
                                                );
                                            }
                                        )
                                    }
                                    // disableFooter={canDrilldown === false}
                                >
                                    <ScoreSummary
                                        improvement={selectedOption.improvement}
                                        score={scoreValue?.score}
                                        prevScoreValue={prevScore}
                                        prevCampaign={prevCampaign}
                                        referenceScore={mappedReferenceScores[selectedOption.scoreId]}
                                        precision={selectedOption.precision ?? 0}
                                        unit={selectedOption.unit}
                                    />
                                </ScoreCard>
                            </Fragment>
                        );
                    })}
                </Box>
            </Stack>
        </Stack>
    );
};
